import { useCallback, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { syncCatalog } from 'utils/posLocalDb/catalogSync'
import { getActiveEndpoint } from 'utils/posLocalDb/queueContext'
import { requestPersistentStorage } from 'utils/posLocalDb/client'

/**
 * Menjalankan sync katalog dengan state progres untuk UI (gate & modal).
 * status: 'idle' | 'running' | 'success' | 'error'; errorType: 'offline' | 'generic'.
 */
const useCatalogSyncRunner = ({ onSuccess } = {}) => {
  const [state, setState] = useState({
    status: 'idle',
    step: 'products',
    errorType: null,
    errorDetail: null,
  })
  const runningRef = useRef(false)
  const stepRef = useRef('products')
  const onSuccessRef = useRef(onSuccess)
  onSuccessRef.current = onSuccess

  const run = useCallback(async () => {
    if (runningRef.current) {
      return
    }
    runningRef.current = true
    stepRef.current = 'products'
    setState({ status: 'running', step: 'products', errorType: null, errorDetail: null })
    try {
      // Minta browser tak menghapus data lokal (antrean transaksi) saat storage penuh.
      await requestPersistentStorage()
      const endpoint = getActiveEndpoint()
      const result = await syncCatalog(endpoint, (step) => {
        stepRef.current = step
        setState((prev) => ({ ...prev, step }))
      })
      if (result.status === 'ok') {
        setState((prev) => ({ ...prev, status: 'success', step: 'done' }))
        onSuccessRef.current?.(endpoint)
        return
      }
      setState((prev) => ({
        ...prev,
        status: 'error',
        errorType: result.reason === 'offline' ? 'offline' : 'generic',
      }))
    } catch (error) {
      Sentry.captureException(error)
      const status = error?.status ?? error?.response?.status
      const detail =
        error?.data?.message || error?.response?.data?.message || error?.message || `${error}`
      setState((prev) => ({
        ...prev,
        status: 'error',
        errorType: 'generic',
        errorDetail: `${status ? `[${status}] ` : ''}${detail}`,
      }))
    } finally {
      runningRef.current = false
    }
  }, [])

  // Lewati langkah gagal hanya aman bila produk sudah tersinkron (gagal di promo).
  const canSkip =
    state.status === 'error' && state.errorType === 'generic' && state.step !== 'products'

  return { ...state, run, canSkip }
}

export default useCatalogSyncRunner
